/**
 * Typed accessors over the loosely-typed multistage-quoting fields of
 * `QuoteResult` (`review` / `manual` / `declined` are open JSON objects in
 * the generated schema) plus the tier/price-semantics helper the pages share.
 *
 * price_cents semantics (backend contract): PER-UNIT for instant quotes,
 * JOB TOTAL for reviewed/manual quotes once the operator has priced them.
 */

import type { Quote, QuoteResult } from "@fprint/shared";

function nonEmptyString(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/** Operator note attached when a reviewed quote was approved/adjusted. */
export function reviewCustomerNote(
  result: QuoteResult | null | undefined,
): string | null {
  return nonEmptyString(result?.review?.["customer_note"]);
}

/** Operator message authored with a manual quote's price. */
export function manualMessage(
  result: QuoteResult | null | undefined,
): string | null {
  return nonEmptyString(result?.manual?.["message"]);
}

/** Reason the operator gave when declining the quote. */
export function declineReason(
  result: QuoteResult | null | undefined,
): string | null {
  return nonEmptyString(result?.declined?.["reason"]);
}

/**
 * True when `result.price_cents` is a JOB TOTAL (reviewed/manual tier, or a
 * quantity was locked by submit-for-review) — it must NOT be multiplied by
 * qty, and the qty control is read-only at `submitted_qty`.
 */
export function isJobTotalPriced(quote: Quote): boolean {
  return (
    quote.tier === "reviewed" ||
    quote.tier === "manual" ||
    quote.submitted_qty != null
  );
}
